export const AGENT_FLAG = "multihopgpt56terrav21low"; // thinking mode (current Explorer default)
export const STANDARD_AGENT_FLAG = "agentv2"; // standard mode (GPT-4.1 single-hop)

export type FinsterQueryMode = "standard" | "thinking";

export const FINSTER_QUERY_MODE_AGENT_FLAGS: Record<FinsterQueryMode, string> = {
  standard: STANDARD_AGENT_FLAG,
  thinking: AGENT_FLAG,
};

export const FEATURE_FLAGS = {
  FACTSET_ACCESS: "factsetAccess",
  FACTSET_ADDITIONAL_DATA_CLIENTS: "useFactsetAdditionalDataClients",
  FACTSET_STREETACCOUNT_NEWS_ACCESS: "factsetStreetaccountNewsAccess",
  FACTSET_INDEX_BENCHMARK_ACCESS: "factsetBenchmarkAccess",
  FACTSET_GLOBAL_FILINGS_ACCESS: "factsetGlobalFilingsAccess",
  FACTSET_AMR_ACCESS: "factsetConversationalAccess",
  FACTSET_RBICS_ACCESS: "factsetRbicsAccess",
  FACTSET_DCS_ACCESS: "factsetDcsAccess",
  FACTSET_TC_ACCESS: "factsetTcAccess",
  ADIA_QUANT_DATA_ACCESS: "adiaQuantDataAccess",
  ADIA_MARKET_DATA_ACCESS: "adiaMarketDataAccess",
  ADDIN_YOUR_FILES_UPLOAD: "addinYourFilesUpload",
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

export const FINSTER_QUERY_DATA_SOURCES = [
  "EARNING_AND_INVESTOR_CALLS",
  "INVESTOR_RELATIONS_DOCUMENTS",
  "SEC_FILINGS",
  "FACTSET_GLOBAL_FILINGS",
  "COMPANY_WEBSITE",
  "MARKET_DATA",
  "FACTSET_FUNDAMENTALS",
  "ESTIMATES",
  "FACTSET_BUSINESS_GEO_SEGMENTS",
  "FACTSET_PEOPLE",
  "FACTSET_MA_DEALS",
  "FACTSET_ETF",
  "FACTSET_PRIVATE_COMPANY",
  "FACTSET_OWNERSHIP",
  "FACTSET_ECONOMIC_EVENTS",
  "FACTSET_STREETACCOUNT_NEWS",
  "FACTSET_INDEX_BENCHMARK",
  "FACTSET_AMR",
  "FACTSET_RBICS",
  "FACTSET_DCS",
  "FACTSET_TC",
  // Agent routes. They stay out of FINSTER_QUERY_DATA_SOURCE_GROUPS so they
  // are not picker choices.
  "ALL_COMPANY_SEARCH",
  "COMPANY_PROFILE",
  "COMPANIES_HOUSE",
  "EARNINGS_BEAT_MISS",
  "EARNINGS_CALENDAR",
  "PRIVATE_MARKETS_DATA",
  "INDUSTRY_RESEARCH",
  "WEB_SEARCH",
  "ADIA_FUND_DATA",
  "ADIA_MARKET_DATA",
  "ADIA_CRM_DOCUMENTS",
  "ADIA_INVESTMENT_PROPOSAL_DOCUMENTS",
  "ADIA_AUDITED_FINANCIAL_STATEMENTS",
] as const;

export type FinsterQueryDataSource = (typeof FINSTER_QUERY_DATA_SOURCES)[number];

/**
 * Narrow source bundle for the low-latency filings/IR research route.
 *
 * The backend applies tenant entitlements, so retaining Global Filings here is
 * safe even when the corresponding feature flag is unavailable.
 */
export const FILINGS_LITE_DATA_SOURCES: readonly FinsterQueryDataSource[] = [
  "SEC_FILINGS",
  "INVESTOR_RELATIONS_DOCUMENTS",
  "FACTSET_GLOBAL_FILINGS",
] as const;

export interface FinsterQueryDataSourceOption {
  label: string;
  param: FinsterQueryDataSource;
  featureFlag?: FeatureFlagKey;
}

export interface FinsterQueryDataSourceGroup {
  label: string;
  featureFlag?: FeatureFlagKey;
  children: readonly FinsterQueryDataSourceOption[];
}

export const FINSTER_QUERY_DATA_SOURCE_GROUPS: readonly FinsterQueryDataSourceGroup[] = [
  {
    label: "Official Company Data",
    children: [
      { label: "Earnings Calls & Investor Conferences", param: "EARNING_AND_INVESTOR_CALLS" },
      {
        label: "Investor Relations Documents & Corporate Presentations",
        param: "INVESTOR_RELATIONS_DOCUMENTS",
      },
      { label: "SEC Filings", param: "SEC_FILINGS" },
      {
        label: "Global Filings",
        param: "FACTSET_GLOBAL_FILINGS",
        featureFlag: "FACTSET_GLOBAL_FILINGS_ACCESS",
      },
      { label: "Company Website", param: "COMPANY_WEBSITE" },
    ],
  },
  {
    label: "FactSet",
    featureFlag: "FACTSET_ACCESS",
    children: [
      { label: "FactSet Market Data", param: "MARKET_DATA" },
      { label: "FactSet Fundamentals", param: "FACTSET_FUNDAMENTALS" },
      { label: "FactSet Consensus Estimates", param: "ESTIMATES" },
      {
        label: "FactSet Business Segment / Geography Revenue Breakdown (Annual)",
        param: "FACTSET_BUSINESS_GEO_SEGMENTS",
      },
      {
        label: "FactSet People & Executives",
        param: "FACTSET_PEOPLE",
        featureFlag: "FACTSET_ADDITIONAL_DATA_CLIENTS",
      },
      {
        label: "FactSet M&A Deals",
        param: "FACTSET_MA_DEALS",
        featureFlag: "FACTSET_ADDITIONAL_DATA_CLIENTS",
      },
      {
        label: "FactSet ETF Reference",
        param: "FACTSET_ETF",
        featureFlag: "FACTSET_ADDITIONAL_DATA_CLIENTS",
      },
      {
        label: "FactSet Private Company Data",
        param: "FACTSET_PRIVATE_COMPANY",
        featureFlag: "FACTSET_ADDITIONAL_DATA_CLIENTS",
      },
      {
        label: "FactSet Ownership Data",
        param: "FACTSET_OWNERSHIP",
        featureFlag: "FACTSET_ADDITIONAL_DATA_CLIENTS",
      },
      {
        label: "FactSet Economic Events",
        param: "FACTSET_ECONOMIC_EVENTS",
        featureFlag: "FACTSET_ADDITIONAL_DATA_CLIENTS",
      },
      {
        label: "FactSet StreetAccount News",
        param: "FACTSET_STREETACCOUNT_NEWS",
        featureFlag: "FACTSET_STREETACCOUNT_NEWS_ACCESS",
      },
      {
        label: "FactSet Index Benchmark",
        param: "FACTSET_INDEX_BENCHMARK",
        featureFlag: "FACTSET_INDEX_BENCHMARK_ACCESS",
      },
      {
        label: "FactSet Sell-Side Entitled Research",
        param: "FACTSET_AMR",
        featureFlag: "FACTSET_AMR_ACCESS",
      },
      {
        label: "FactSet RBICS Industry Classification",
        param: "FACTSET_RBICS",
        featureFlag: "FACTSET_RBICS_ACCESS",
      },
      {
        label: "FactSet Debt Capital Structure",
        param: "FACTSET_DCS",
        featureFlag: "FACTSET_DCS_ACCESS",
      },
      {
        label: "FactSet Terms & Conditions",
        param: "FACTSET_TC",
        featureFlag: "FACTSET_TC_ACCESS",
      },
    ],
  },
  {
    label: "Industry Research",
    children: [{ label: "Industry Research", param: "INDUSTRY_RESEARCH" }],
  },
  {
    label: "Web Search",
    children: [{ label: "Web Search", param: "WEB_SEARCH" }],
  },
  {
    label: "ADIA",
    featureFlag: "ADIA_QUANT_DATA_ACCESS",
    children: [
      { label: "Fund Data", param: "ADIA_FUND_DATA" },
      { label: "Market Data", param: "ADIA_MARKET_DATA", featureFlag: "ADIA_MARKET_DATA_ACCESS" },
      { label: "CRM Documents", param: "ADIA_CRM_DOCUMENTS" },
      {
        label: "Investment Proposal Documents",
        param: "ADIA_INVESTMENT_PROPOSAL_DOCUMENTS",
      },
      { label: "Audited Financial Statements", param: "ADIA_AUDITED_FINANCIAL_STATEMENTS" },
    ],
  },
];

export type FeatureFlags = Partial<Record<FeatureFlagKey, boolean>>;

export function isDataSourceFeatureEnabled(
  featureFlags: FeatureFlags | undefined,
  featureFlag?: FeatureFlagKey
): boolean {
  return !featureFlag || featureFlags?.[featureFlag] === true;
}

export function isAddinYourFilesUploadEnabled(
  featureFlags: FeatureFlags | undefined,
  featureFlagsLoading = false
): boolean {
  if (featureFlagsLoading) return false;
  return featureFlags?.ADDIN_YOUR_FILES_UPLOAD === true;
}

export function getAvailableFinsterQueryDataSourceGroups(
  featureFlags?: FeatureFlags,
  featureFlagsLoading = false
): FinsterQueryDataSourceGroup[] {
  if (featureFlagsLoading) {
    return [];
  }

  return FINSTER_QUERY_DATA_SOURCE_GROUPS.flatMap((group) => {
    if (!isDataSourceFeatureEnabled(featureFlags, group.featureFlag)) {
      return [];
    }

    const children = group.children.filter((child) =>
      isDataSourceFeatureEnabled(featureFlags, child.featureFlag)
    );

    return children.length > 0 ? [{ ...group, children }] : [];
  });
}

export function getAvailableFinsterQueryDataSources(
  featureFlags?: FeatureFlags,
  featureFlagsLoading = false
): FinsterQueryDataSource[] {
  return getAvailableFinsterQueryDataSourceGroups(featureFlags, featureFlagsLoading).flatMap((group) =>
    group.children.map((child) => child.param)
  );
}

export const FINSTER_QUERY_DATA_SOURCES_TEXT = FINSTER_QUERY_DATA_SOURCES.join(", ");

export type PublicDataSourceBrandName = "Finster" | "FactSet AI";

export function getAutomaticDataSourceSelectionText(
  publicBrandName: PublicDataSourceBrandName = "Finster"
): string {
  return `no data sources explicitly selected; ${publicBrandName} will choose the best sources automatically`;
}

export function getFinsterQueryDataSourcesText(
  dataSources?: readonly FinsterQueryDataSource[],
  publicBrandName: PublicDataSourceBrandName = "Finster"
): string {
  if (!dataSources) {
    return FINSTER_QUERY_DATA_SOURCES_TEXT;
  }

  if (dataSources.length === 0) {
    return getAutomaticDataSourceSelectionText(publicBrandName);
  }

  return dataSources.join(", ");
}
